import { effect } from '@preact/signals';
import { render } from 'preact';
import { App } from './App';
import widgetCss from './styles/widget.css?inline';
import webWidgetCss from './styles/web-widget.css?inline';
import { EMOJI_FONT_FACE } from './emoji-font';
import { initLocale } from './hooks/use-i18n';
import {
  config,
  ready,
  widgetClientId,
  draggedBubblePos,
  webPanelPos,
  webPanelMaxPos,
  digitalModalPos,
  fullPage,
  webMaximized,
  setOpen,
  openDigitalWelcome,
} from './store/widget-store';
import { readStoredPlacement, readPlacement } from './services/widget-position-store';
import { applyTheme } from './services/apply-theme';
import { applyLayout } from './services/apply-layout';
import { installWidgetApi, ensureInvokeEvent } from './services/widget-api';
import { fetchWidgetConfig, WidgetConfigError } from './services/widget-config-client';
import { preloadDigitalPoster } from './services/poster-preload';
import { configureWsClient, setWsClientLocale } from './services/ws-client';
import { readHostToken } from './services/host-auth';
import { configureImClient, setImClientLanguage, setImClientClientIp } from './services/vocechat-client';
import { resolveImBridgeUrl } from './services/im-socket';
import { fetchClientIp } from './services/client-ip';
import { currentLocale } from './store/widget-store';
import { installUnreadNotifier } from './services/unread-notifier';
import { dcsLog } from './services/logger';

/**
 * 注入 Twemoji 彩色 emoji 子集字体的 @font-face 到 document.head。
 * - @font-face 是文档级资源,注册到主文档后 Shadow DOM 内引用同名字体最稳
 *   (规避旧浏览器 shadow 内 @font-face 不生效的坑)。字体名 DCSTwemoji 带前缀,
 *   子集只含 emoji 字形,不影响宿主页面普通文字渲染。
 * - 幂等:同页多次 bootstrap / 误嵌多份 widget 只注册一次。
 */
function ensureEmojiFont(): void {
  const ID = 'dcs-emoji-font';
  if (document.getElementById(ID)) return;
  const style = document.createElement('style');
  style.id = ID;
  style.textContent = EMOJI_FONT_FACE;
  document.head.appendChild(style);
}

function ensureHost(): HTMLElement {
  let host = document.getElementById('dcs-widget-root');
  if (!host) {
    host = document.createElement('div');
    host.id = 'dcs-widget-root';
    document.body.appendChild(host);
  }
  return host;
}

function readOrCreateFingerprint(): string {
  const KEY = 'dcs.widget.fingerprint';
  try {
    const existing = localStorage.getItem(KEY);
    if (existing) return existing;
    const fresh =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `fp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(KEY, fresh);
    return fresh;
  } catch {
    // 隐私模式 / 第三方 cookie 拦截：用一个会话内 stable 的值
    return `mem-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

function readOrCreateVisitorId(): string {
  // vocechat 访客 SDK 要求浏览器持久化唯一 visitor_id(推荐 vst_<uuid>)
  const KEY = 'dcs.widget.visitor_id';
  const gen = () =>
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? `vst_${crypto.randomUUID()}`
      : `vst_${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  try {
    const existing = localStorage.getItem(KEY);
    if (existing) return existing;
    const fresh = gen();
    localStorage.setItem(KEY, fresh);
    return fresh;
  } catch {
    // 隐私模式:退化为会话内 stable 值(刷新会变,但不阻塞)
    return gen();
  }
}

function readClientId(): string | null {
  // 客户嵌入: <script src=".../widget.js" data-client-id="xxx"></script>
  // 取最后一个匹配 widget.js 的 <script>,与 widget-loader 约定一致
  const scripts = Array.from(document.scripts);
  for (let i = scripts.length - 1; i >= 0; i--) {
    const s = scripts[i];
    if (s.src && /widget\.js(?:\?|$)/.test(s.src)) {
      const id = s.dataset.clientId ?? s.getAttribute('data-client-id');
      if (id) return id;
    }
  }
  return null;
}

/**
 * 整页 H5 宿主标记 — 读同一个 widget.js <script> 上的 data-full-page。
 * 由 H5 宿主页(public/h5.html,供小程序 webview 引入)注入 data-full-page="1" 时返回 true:
 * 触发整页全屏模式(铺满 + 隐藏入口 + config 就绪后自动展开,见 bootstrap)。
 * 取值 '1' / 'true' / 空串(仅写属性名)均视为开启;缺省 / 其他值视为关闭。
 */
function readFullPageFlag(): boolean {
  const scripts = Array.from(document.scripts);
  for (let i = scripts.length - 1; i >= 0; i--) {
    const s = scripts[i];
    if (s.src && /widget\.js(?:\?|$)/.test(s.src)) {
      const v = s.dataset.fullPage ?? s.getAttribute('data-full-page');
      if (v != null) return v === '1' || v === 'true' || v === '';
    }
  }
  return false;
}

async function bootstrap() {
  initLocale();

  const host = ensureHost();
  if (host.dataset.dcsBootstrapped === '1') {
    // 已挂载 — 防止重复调用 bootstrap() 造成 effect/render 双注册(开发 HMR / 客户误嵌两份 script)
    return;
  }
  host.dataset.dcsBootstrapped = '1';

  ensureEmojiFont();

  const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });

  const styleEl = document.createElement('style');
  styleEl.textContent = widgetCss + '\n' + webWidgetCss;
  shadow.appendChild(styleEl);

  const mount = document.createElement('div');
  shadow.appendChild(mount);

  // 主题色注入 — config.theme 变化时同步到 :host 的 CSS 变量
  // 注:effect 返回 unsubscribe,widget 生命周期 = 页面生命周期,无需 cleanup
  effect(() => {
    applyTheme(host, config.value?.theme ?? null, config.value?.titleBgGradient ?? null);
  });
  // 位置/偏移注入 — config 变化时同步 --dcs-offset-* 与 data-dcs-pos 到 :host
  effect(() => {
    applyLayout(host, config.value);
  });
  // 整页 H5 宿主模式 — 把 fullPage 写到 :host 的 data-dcs-fullpage 属性,供面板/沉浸层 CSS 强制铺满。
  // 沿用 applyLayout 往 :host 写属性的先例;fullPage 在下方 bootstrap 分支中按 data-full-page 置位。
  effect(() => {
    if (fullPage.value) host.setAttribute('data-dcs-fullpage', '1');
    else host.removeAttribute('data-dcs-fullpage');
  });
  // 外部唤起:装 window.DCSWidget API + 默认事件监听(立即可用);
  // config ready 后再补挂后台配置的自定义 open 事件名。
  installWidgetApi();
  effect(() => {
    ensureInvokeEvent(config.value?.invokeEventName);
  });

  // 后台未读提醒:AI/真人有新消息且访客最小化或切到别的窗口时,
  // 标题闪烁 + 系统通知 + favicon 红点 + 提示音(全局一次性,生命周期=页面)。
  installUnreadNotifier();

  if (import.meta.env.VITE_USE_MOCK === 'true') {
    // mock 预览无嵌入 script,用 'mock' 占位 clientId,使拖动位置缓存可演示
    widgetClientId.value = 'mock';
    // dev 便利:mock 下也读 data-full-page,使本地能预览整页全屏(需 mock config 已就绪)
    fullPage.value = readFullPageFlag();
    draggedBubblePos.value = readStoredPlacement(widgetClientId.value);
    // 框体(普通/最大化网页面板、数字人弹窗)各自的拖动落点,按 clientId+slot 隔离、24h 过期
    const mockNow = Date.now();
    webPanelPos.value = readPlacement(widgetClientId.value, 'web-panel', mockNow);
    webPanelMaxPos.value = readPlacement(widgetClientId.value, 'web-panel-max', mockNow);
    digitalModalPos.value = readPlacement(widgetClientId.value, 'digital-modal', mockNow);
    const mod = await import('./mocks');
    await mod.setupMocks();
    if (fullPage.value && config.value) {
      if (config.value.type === 'digital') openDigitalWelcome();
      else {
        webMaximized.value = true;
        setOpen(true);
      }
    }
  } else {
    const clientId = readClientId();
    if (!clientId) {
      dcsLog.error(
        'bootstrap',
        '缺少 data-client-id — <script src=".../widget.js" data-client-id="..."> 必须配置',
      );
      return;
    }
    const apiBase = import.meta.env.VITE_API_BASE_URL as string | undefined;
    if (!apiBase) {
      dcsLog.error('bootstrap', 'VITE_API_BASE_URL 未配置');
      return;
    }
    // 迷你按钮拖动位置 / 提示冷却按 clientId 隔离;优先读缓存落点(无则回落服务端配置)
    widgetClientId.value = clientId;
    draggedBubblePos.value = readStoredPlacement(clientId);
    // 框体(普通/最大化网页面板、数字人弹窗)各自的拖动落点,同样按 clientId+slot 隔离、24h 过期
    const posNow = Date.now();
    webPanelPos.value = readPlacement(clientId, 'web-panel', posNow);
    webPanelMaxPos.value = readPlacement(clientId, 'web-panel-max', posNow);
    digitalModalPos.value = readPlacement(clientId, 'digital-modal', posNow);
    // 整页 H5 宿主标记(宿主页 <script data-full-page="1">):驱动整页全屏 + config 就绪后自动展开
    const wantFullPage = readFullPageFlag();
    fullPage.value = wantFullPage;
    try {
      const cfg = await fetchWidgetConfig({ apiBase, clientId });
      config.value = cfg;
      ready.value = true;
      dcsLog.info('bootstrap', `config 就绪 type=${cfg.type} audioEnabled=${cfg.audioEnabled}`);
      // 数字人:启动即预载加载期海报(焐热 OSS 转码/CDN/浏览器缓存),悬浮球入口待其
      // 落定(digitalEntryReady)再显示,首访点开无白屏;程序化唤起绕行、不受影响。
      if (cfg.type === 'digital') preloadDigitalPoster(cfg);
      // 整页模式:config 就绪同步直接展开(in-process,无事件竞态;此时 config.value 已非空)。
      // web → 最大化态(配合 :host[data-dcs-fullpage] CSS 100dvh 铺满);digital → 欢迎屏(沉浸全屏)。
      if (wantFullPage) {
        if (cfg.type === 'digital') openDigitalWelcome();
        else {
          webMaximized.value = true;
          setOpen(true);
        }
      }
      // 配置 SSE+POST 传输层（session 在第一次 emit/connect 时按需建立）
      configureWsClient({
        apiBase,
        clientId,
        fingerprint: readOrCreateFingerprint(),
        locale: currentLocale.value,
        // 会话发起页：widget 以 Shadow DOM 注入宿主同文档，location.href 即宿主页真实 URL；
        // bootstrap 时固化，建会话时上报，推主站留资 sourceUrl。
        pageUrl: location.href,
        // 宿主商城登录 token 取值器：发消息时即时读 localStorage['mall:auth'].token
        hostTokenProvider: readHostToken,
      });
      // 访客在面板切换语言后,后续 AI 消息须带新语言码(后端据此更新 conversation.locale,
      // AI 回复 / 数字人 TTS 随之切换)。与下方 IM 的 setImClientLanguage 同款:用 effect
      // 跟随 currentLocale,而非 bootstrap 时一次性固化。
      effect(() => {
        setWsClientLocale(currentLocale.value);
      });
      // 人工 IM(vocechat)链路:web 与 digital 客服且后端下发 im.enabled 时配置。
      // 实际连接由 WebChatPanel / 数字人 ChatPane 切到人工模式时按需 refreshServiceStatus()。
      dcsLog.info('boot', 'IM 配置判定', {
        type: cfg.type,
        imEnabled: cfg.im?.enabled,
        imBaseUrl: cfg.im?.baseUrl,
      });
      if ((cfg.type === 'web' || cfg.type === 'digital') && cfg.im?.enabled && cfg.im.baseUrl) {
        dcsLog.info('boot', 'configureImClient 已调用');
        configureImClient({
          baseUrl: cfg.im.baseUrl,
          socketBridgeUrl: resolveImBridgeUrl(cfg.widgetBundleUrl, location.origin),
          agentId: clientId,
          visitorId: readOrCreateVisitorId(),
          pageUrl: location.href,
          pageTitleProvider: () => document.title,
          hostTokenProvider: readHostToken,
          // 初始取 widget 实际生效语言(detectLocale 已含 navigator.language 归一),
          // 而非裸 navigator.language;后续切换由下方 effect 同步。
          language: currentLocale.value,
        });
        // 真实公网 IP best-effort 异步回填(不阻塞 widget 渲染):IM 连接在面板切人工模式时
        // 才 connect()→fetchStatus,届时 IP 多已就绪;拿不到则不带,IM 回退用入口连接 IP。
        void fetchClientIp().then((ip) => {
          if (ip) setImClientClientIp(ip);
        });
        // 访客在面板切换语言后,后续 IM 消息须带新语言码(IM 侧据此翻译给坐席)。
        // 用 effect 跟随 currentLocale,而非 bootstrap 时一次性固化。
        effect(() => {
          setImClientLanguage(currentLocale.value);
        });
      }
    } catch (e) {
      if (e instanceof WidgetConfigError) {
        dcsLog.error(
          'bootstrap',
          `config 加载失败 ${e.status} ${e.code ?? ''}: ${e.message}`,
        );
      } else {
        dcsLog.error('bootstrap', 'config 加载异常', e);
      }
      return;
    }
  }

  render(<App />, mount);
}

bootstrap().catch((err) => {
  dcsLog.error('bootstrap', '启动失败', err);
});
